// Consumer-facing tripsApi module.
//
// `tripsApi.generated.ts` is auto-generated from the OpenAPI spec. This file
// wraps it, applies runtime endpoint enhancements (tag overrides), and
// re-exports everything. All code that needs trip endpoints/types/hooks
// should import from here, not from the generated file directly — that way
// the enhancements are always applied exactly once, at the first import of
// any trip symbol.
//
// Doing this here (instead of in enhancedBambiApi.ts) keeps tripsApi off the
// import graph of the shared bambiApi bootstrap, which is a prerequisite for
// lazy-loading trip-feature chunks.

import { bambiApi } from './tripsApi.generated';
import type {
  BulkTripUnassignDeltaResultsRead,
  DispatchDeltaRead,
} from './tripsApi.generated';
import type { RootState } from 'src/app/store/types';

/**
 * Whether the caller passed the board's filters, which is what makes the delta
 * describe a board it can be merged into. Omitting them opts out: no patch,
 * and 'dispatch' still invalidated so the board reloads the old way. The
 * multi-load assign flow relies on that (see useMultiloadAssign).
 */
type DispatchDeltaArg = { rangeStart?: string };
const isMergeable = (arg: DispatchDeltaArg) => Boolean(arg.rangeStart);

/**
 * Shared handling for the trip actions that answer with a dispatch delta
 * (DispatchDeltaResponseMixin on the backend).
 *
 * When the response carries the delta, the dispatch cache is patched in place
 * — skip 'dispatch' so it doesn't also refetch, but keep 'trips' so trip grids
 * elsewhere stay fresh (dispatchRetrieve deliberately doesn't provide 'trips';
 * see enhancedBambiApi). The status counts are the one board-wide aggregate the
 * delta can't carry, so they refetch on their own tag. A body without an
 * `assignments` field (backend not deployed / rolled back, or a delta that
 * failed to build) falls back to today's invalidation refetch, which covers
 * the counts via 'dispatch'. Presence, not length: a delta whose only visible
 * content is an unassigned row legitimately carries `assignments: []` when the
 * calendar filters hide the vacated crew.
 *
 * The lifecycle arguments are spelled out structurally rather than inferred,
 * since one definition has to satisfy several endpoints' contexts.
 */
const deltaTags = (arg: DispatchDeltaArg, delta: DispatchDeltaRead | undefined) =>
  isMergeable(arg) && delta?.assignments
    ? (['trips', 'run-bambi-run', 'dispatch-summary'] as const)
    : (['trips', 'dispatch', 'run-bambi-run'] as const);

// RTK types the store as unknown in onQueryStarted; the cast below is where
// that meets our own state type.
type DeltaLifecycle = {
  dispatch: (action: unknown) => unknown;
  getState: () => unknown;
};

const mergeDeltaWhenMergeable = async (
  arg: DispatchDeltaArg,
  { dispatch, getState }: DeltaLifecycle,
  pendingDelta: Promise<DispatchDeltaRead | undefined>
) => {
  if (!isMergeable(arg)) {
    return;
  }
  // Read before the await: the delta describes the board this request was
  // cut for, and the dispatcher can change date or filters while it is in
  // flight. Redux state is immutable, so this stays that board.
  const stateAtRequest = getState() as RootState;
  let delta;
  try {
    delta = await pendingDelta;
  } catch {
    return; // error path reconciles via the fallback invalidation
  }
  if (!delta?.assignments) {
    return;
  }
  try {
    // Dynamic import: a static one would cycle (tripsApi →
    // dispatchCacheThunks → dispatcher.slice → tripsApi) and pull the
    // dispatch feature into this lazy-loadable module's graph.
    const { applyDispatchDelta } = await import(
      'src/features/dispatch/common/dispatchCacheThunks'
    );
    dispatch(applyDispatchDelta(delta, stateAtRequest));
  } catch {
    // The chunk can fail to load (asset rollover mid-deploy), and by now the
    // mutation's tags already skipped 'dispatch' — invalidate it by hand so
    // the board refetches instead of going stale.
    dispatch(bambiApi.util.invalidateTags(['dispatch']));
  }
};

const dispatchDeltaEndpoint = {
  invalidatesTags: (
    result: DispatchDeltaRead | undefined,
    _error: unknown,
    arg: DispatchDeltaArg
  ) => deltaTags(arg, result),
  onQueryStarted: (
    arg: DispatchDeltaArg,
    {
      queryFulfilled,
      ...lifecycle
    }: DeltaLifecycle & { queryFulfilled: Promise<{ data: DispatchDeltaRead }> }
  ) =>
    mergeDeltaWhenMergeable(
      arg,
      lifecycle,
      queryFulfilled.then(({ data }) => data)
    ),
};

/**
 * Bulk unassign can decline trips and reports them, so its delta rides in the
 * body's `delta` field instead of being the body (see
 * BulkTripUnassignDeltaResultsSerializer). Same contract otherwise.
 */
const bulkUnassignDeltaEndpoint = {
  invalidatesTags: (
    result: BulkTripUnassignDeltaResultsRead | undefined,
    _error: unknown,
    arg: DispatchDeltaArg
  ) => deltaTags(arg, result?.delta),
  onQueryStarted: (
    arg: DispatchDeltaArg,
    {
      queryFulfilled,
      ...lifecycle
    }: DeltaLifecycle & {
      queryFulfilled: Promise<{ data: BulkTripUnassignDeltaResultsRead }>;
    }
  ) =>
    mergeDeltaWhenMergeable(
      arg,
      lifecycle,
      queryFulfilled.then(({ data }) => data.delta)
    ),
};

bambiApi.enhanceEndpoints({
  // Declared on enhancedBambiApi too; both enhance the same root api, and this
  // call needs it in scope for the invalidation above to typecheck.
  addTagTypes: ['dispatch-summary'],
  endpoints: {
    tripsAssignPartialUpdate: dispatchDeltaEndpoint,
    tripsReassignPartialUpdate: dispatchDeltaEndpoint,
    tripsBulkAssignCreate: dispatchDeltaEndpoint,
    tripsBulkReassignCreate: dispatchDeltaEndpoint,
    tripsValidatePartialUpdate: {
      // We don't want to actually invalidate trips, dispatch, or run-bambi-run
      // tagged queries on this update. See https://bambihealth.atlassian.net/browse/BAM-793
      // and related PRs. Prevents a race where /dispatch/ is invalidated after
      // a /validate/ call and refetched before /assign/ or /reassign/
      // completes, producing stale UI.
      invalidatesTags: [],
    },
    tripsMultiloadCreate: {
      invalidatesTags: ['dispatch', 'trips'],
    },
    tripsMultiloadPartialUpdate: {
      invalidatesTags: ['dispatch', 'trips'],
    },
    tripsMultiloadDestroy: {
      invalidatesTags: ['dispatch', 'trips'],
    },
    tripsUnassignPartialUpdate: dispatchDeltaEndpoint,
    tripsBulkUnassignCreate: bulkUnassignDeltaEndpoint,
    // This doesn't actually change data.
    tripsBulkValidateCreate: {
      invalidatesTags: [],
    },
    tripsBulkPriceCreate: {
      // This was causing the trip list to refresh. No trip data has been
      // changed at this point, so no need to invalidate any tags.
      invalidatesTags: [],
    },
  },
});

export * from './tripsApi.generated';
